import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../context/CartContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { cartCount, setIsCartOpen } = useCart();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const links = [
    { name: 'Collections', path: '/shop' },
    { name: 'Our Process', path: '/info' },
  ];

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
      scrolled ? 'bg-frost-vanilla/80 backdrop-blur-lg shadow-lg py-3' : 'bg-transparent py-5'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="bg-frost-berry p-2 rounded-xl group-hover:rotate-12 transition-transform duration-300">
              <SafeIcon icon="FiWind" className="text-2xl text-white" />
            </div>
            <span className="font-serif text-2xl font-black tracking-tighter text-frost-berry">
              FrostBox
            </span>
          </Link>

          <div className="hidden md:flex items-center space-x-10">
            {links.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-sm font-bold uppercase tracking-widest transition-colors relative group ${
                  location.pathname === link.path ? 'text-frost-berry' : 'text-frost-dark hover:text-frost-berry'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-frost-berry transition-all duration-300 group-hover:w-full ${
                  location.pathname === link.path ? 'w-full' : ''
                }`}></span>
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <button 
              onClick={() => setIsCartOpen(true)}
              className="relative p-3 bg-frost-berry text-white rounded-2xl shadow-lg shadow-frost-berry/20 hover:scale-105 active:scale-95 transition-all"
              aria-label={`Cart with ${cartCount} items`}
            >
              <SafeIcon icon="FiShoppingCart" className="text-xl" />
              <AnimatePresence>
                {cartCount > 0 && (
                  <motion.span 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute -top-1 -right-1 bg-white text-frost-berry text-[10px] font-black rounded-full h-5 w-5 flex items-center justify-center border-2 border-frost-berry"
                  >
                    {cartCount}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
            <button 
              onClick={() => setIsOpen(!isOpen)} 
              className="md:hidden p-3 text-frost-berry bg-frost-ice rounded-2xl"
              aria-label="Toggle menu"
            >
              <SafeIcon icon={isOpen ? "FiX" : "FiMenu"} className="text-xl" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-full left-0 w-full bg-white shadow-2xl border-t border-frost-ice md:hidden"
          >
            <div className="px-6 py-10 space-y-6">
              {links.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className="block text-2xl font-serif font-bold text-frost-dark hover:text-frost-berry transition-colors"
                >
                  {link.name}
                </Link>
              ))}
              <hr className="border-frost-ice" />
              <button className="btn-primary w-full">Sign In for Preorders</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;